import { getPaletteColor } from "@/metrics/compare-by"
import type { MetricRecord } from "@/metrics/runtime"
import {
  compareGroupLabels,
  getGroupKeysForRow,
  type CompareBy,
} from "@/run-detail/compare-by"

import type { RunRowLike } from "@/types/RunRow"
import { formatNumber } from "@/utils/formatting/format-number"

// Single-series color when the report has no active Compare By. Sourced from the
// Compare By palette so ungrouped plots match grouped coloring.
const SINGLE_SERIES_COLOR = getPaletteColor(0, 1)

type MetricRecordsById =
  | Record<string, MetricRecord | null | undefined>
  | undefined

export type ScatterAxisKind = "numeric" | "categorical"

export interface ScatterAxis {
  kind: ScatterAxisKind
  // Index -> label for a categorical axis; empty for a numeric axis.
  categories: string[]
}

export interface ScatterPoint {
  simulationOutputId: string
  // Plotted coordinates: the raw value for a numeric axis, the category's
  // ordinal index for a categorical one.
  x: number
  y: number
  // Optional 3rd metric driving bubble size; absent when no z metric is set or
  // the sim has no value for it.
  z?: number
  // Human-readable value for the tooltip (category name or formatted number).
  xDisplay: string
  yDisplay: string
  color: string
  // Compare By group this point represents. On analyticsPoints this is the
  // row's first membership (stable, arbitrary — analytics ignore it). On
  // renderPoints a multi-tag row emits one entry per membership so the plot
  // and legend show every group.
  groupKey: string | null
  groupLabel: string | null
}

interface ScatterDataset {
  // One entry per simulation. Feeds Tukey outlier fences, the least-squares
  // trend fit, the footer point count, and the bubble-size (z) domain — all
  // of which should weight each simulation exactly once, regardless of how
  // many groups the row belongs to.
  analyticsPoints: ScatterPoint[]
  // Fanned per group membership: a two-tag row emits two entries at the same
  // coordinates so the scatter draws it once per group and the legend
  // enumerates every group. Consumed by <Scatter> and buildLegendEntries.
  renderPoints: ScatterPoint[]
  xAxis: ScatterAxis
  yAxis: ScatterAxis
}

// Categorical metrics store their label in string_value; everything else plots
// its float_value.
function axisKind(record: MetricRecord | null | undefined): ScatterAxisKind {
  return record?.output_type === "string" ? "categorical" : "numeric"
}

function rawAxisValue(
  row: RunRowLike,
  metricId: string,
  kind: ScatterAxisKind,
): number | string | null {
  const output = row.metric_outputs[metricId]
  if (!output) {
    return null
  }
  if (kind === "categorical") {
    const value = output.string_value
    return value != null && value !== "" ? value : null
  }
  const value = output.float_value
  return typeof value === "number" && Number.isFinite(value) ? value : null
}

function sortedDistinct(values: string[]): string[] {
  return Array.from(new Set(values)).sort(compareGroupLabels)
}

function emptyDataset(): ScatterDataset {
  return {
    analyticsPoints: [],
    renderPoints: [],
    xAxis: { kind: "numeric", categories: [] },
    yAxis: { kind: "numeric", categories: [] },
  }
}

// One point per simulation that has a value for both axes. Numeric axes plot the
// float value; categorical axes map each distinct label to an evenly-spaced
// ordinal index. Rows missing either value (errored, skipped, or not run) drop.
// A z metric, if set, adds bubble-size data; sims missing it keep the point.
export function buildScatterDataset(
  runRows: RunRowLike[],
  xMetricId: string | null | undefined,
  yMetricId: string | null | undefined,
  compareBy: CompareBy | null,
  metricRecordsById: MetricRecordsById,
  zMetricId?: string | null,
): ScatterDataset {
  if (!xMetricId || !yMetricId) {
    return emptyDataset()
  }
  const xKind = axisKind(metricRecordsById?.[xMetricId])
  const yKind = axisKind(metricRecordsById?.[yMetricId])

  const rows: {
    row: RunRowLike
    rawX: number | string
    rawY: number | string
  }[] = []
  for (const row of runRows) {
    const rawX = rawAxisValue(row, xMetricId, xKind)
    const rawY = rawAxisValue(row, yMetricId, yKind)
    if (rawX === null || rawY === null) {
      continue
    }
    rows.push({ row, rawX, rawY })
  }

  const xCategories =
    xKind === "categorical"
      ? sortedDistinct(rows.map((r) => r.rawX as string))
      : []
  const yCategories =
    yKind === "categorical"
      ? sortedDistinct(rows.map((r) => r.rawY as string))
      : []
  const xIndex = new Map(xCategories.map((label, i) => [label, i]))
  const yIndex = new Map(yCategories.map((label, i) => [label, i]))

  const analyticsPoints: ScatterPoint[] = []
  const renderPoints: ScatterPoint[] = []
  for (const { row, rawX, rawY } of rows) {
    // Categorical raw values are always present in the index (built from these
    // same rows); the ?? keeps the type number without a non-null assertion.
    const x =
      xKind === "categorical"
        ? (xIndex.get(rawX as string) ?? 0)
        : (rawX as number)
    const y =
      yKind === "categorical"
        ? (yIndex.get(rawY as string) ?? 0)
        : (rawY as number)
    const zRaw = zMetricId
      ? row.metric_outputs[zMetricId]?.float_value
      : undefined
    const z =
      typeof zRaw === "number" && Number.isFinite(zRaw) ? zRaw : undefined
    const xDisplay =
      xKind === "categorical"
        ? (rawX as string)
        : formatNumber(rawX as number, 3)
    const yDisplay =
      yKind === "categorical"
        ? (rawY as string)
        : formatNumber(rawY as number, 3)
    // Resolve the row's memberships once — analytics take only the first
    // (arbitrary, stable) so a multi-tag simulation counts exactly once in
    // quantiles, trend fits, and the footer count. renderPoints fan out one
    // entry per membership so the plot and legend see every group. Rows
    // with no membership under the active Compare By still plot once as
    // ungrouped (single-series color), matching how they look with Compare
    // By off.
    const memberships = compareBy ? getGroupKeysForRow(row, compareBy) : []
    const groupKeys: (string | null)[] =
      memberships.length > 0 ? memberships : [null]
    const buildPoint = (groupKey: string | null): ScatterPoint => {
      const color =
        groupKey != null
          ? (compareBy?.colors[groupKey] ?? SINGLE_SERIES_COLOR)
          : SINGLE_SERIES_COLOR
      const groupLabel =
        groupKey != null
          ? (compareBy?.displayLabels?.[groupKey] ?? groupKey)
          : null
      return {
        simulationOutputId: row.simulation_output_id,
        x,
        y,
        ...(z !== undefined ? { z } : {}),
        xDisplay,
        yDisplay,
        color,
        groupKey,
        groupLabel,
      }
    }
    analyticsPoints.push(buildPoint(groupKeys[0]))
    for (const groupKey of groupKeys) {
      renderPoints.push(buildPoint(groupKey))
    }
  }

  return {
    analyticsPoints,
    renderPoints,
    xAxis: { kind: xKind, categories: xCategories },
    yAxis: { kind: yKind, categories: yCategories },
  }
}
